import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Clock } from 'lucide-react';

const HourlyToSalaryCalculatorTool = () => {
  const [hourlyRate, setHourlyRate] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState('40');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const rate = parseFloat(hourlyRate);
    const hours = parseFloat(hoursPerWeek);

    if (isNaN(rate) || isNaN(hours) || rate < 0 || hours <= 0) {
      setResult(null);
      return;
    }

    // Calculate salary from hourly rate
    const weekly = rate * hours;
    const biweekly = weekly * 2;
    const monthly = (weekly * 52) / 12;
    const annual = weekly * 52;
    const totalHoursYear = hours * 52;

    setResult({
      hourlyRate: rate,
      hoursPerWeek: hours,
      weekly,
      biweekly,
      monthly,
      annual,
      totalHoursYear
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setHourlyRate('');
    setHoursPerWeek('40');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Hourly Rate ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="e.g., 25"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="hourly-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Hours per Week
            </label>
            <input
              type="number"
              step="0.5"
              value={hoursPerWeek}
              onChange={(e) => setHoursPerWeek(e.target.value)}
              placeholder="e.g., 40"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="hours-per-week-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-hourly-btn"
      >
        <Calculator size={18} />
        Calculate Salary
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="hourly-results">
          {/* Main Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Monthly Salary</span>
                <button
                  onClick={() => copyResult(result.monthly, 'monthly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'monthly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-monthly"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="monthly-result">
                {formatCurrency(result.monthly)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Annual Salary</span>
                <button
                  onClick={() => copyResult(result.annual, 'annual')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'annual' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-annual"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-blue-400" data-testid="annual-result">
                {formatCurrency(result.annual)}
              </div>
            </div>
          </div>

          {/* Additional Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Weekly</span>
                <button
                  onClick={() => copyResult(result.weekly, 'weekly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'weekly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-xl font-bold text-yellow-400">
                {formatCurrency(result.weekly)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Bi-weekly</span>
                <button
                  onClick={() => copyResult(result.biweekly, 'biweekly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'biweekly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-xl font-bold text-purple-400">
                {formatCurrency(result.biweekly)}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Calculation Summary</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Hourly Rate</span>
                <span className="text-white font-medium">{formatCurrency(result.hourlyRate)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Hours per Week</span>
                <span className="text-white font-medium">{result.hoursPerWeek} hours</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Hours per Year</span>
                <span className="text-white font-medium">{result.totalHoursYear.toLocaleString()} hours</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Weekly (rate × hours)</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.weekly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly (weekly × 52 ÷ 12)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.monthly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Annual (weekly × 52)</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.annual)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Common Work Schedules</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Full-time</div>
            <div>40 hours/week</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Part-time</div>
            <div>20-30 hours/week</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Overtime</div>
            <div>45+ hours/week</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Note</h3>
        <div className="text-sm text-gray-400">
          This calculates gross pay before taxes. Assumes 52 working weeks per year. Actual take-home pay will be lower after taxes and deductions.
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-hourly-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default HourlyToSalaryCalculatorTool;
